import React from 'react';

import emotionStyled from '@emotion/styled';
import { PlayIcon } from 'components/PlayIcon';
import { getRiskEstimationColor } from 'screens/Levelup/components/common';
import { EmptyBar } from 'screens/analytics/commonComponents';
import styled, { css } from 'styled-components';
import { FlexContainer, StyledGrid, StyledIconButton, VerticallyAlignedFlexContainer } from 'styles/common';

import { ArrowRightCircle } from 'assets/assetsV1/iconComponents';

import theme from 'constants/theme';

import { Typography12, Typography13, Typography14 } from 'uiToolkit/Typography';
import { Avatar } from 'uiToolkitV1/Avatar';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useLevelUpAnalytics } from '../context';
import LevelUpAnalyticsLineMaker from './LevelUpAnalyticsLineMaker';

function makeNegativeNumberZero(num) {
  return num < 0 ? 0 : num;
}

export const LevelUpRiskEstimationCountCellForAnalytics = ({ riskEstimation }) => {
  const {
    state: { riskEstimationList = [] },
  } = useLevelUpAnalytics();

  const riskEstimationType = Object?.keys(riskEstimation)[0]; // {NEEDS_ATTENTION: { count: 1212...}} extracting the key "NEEDS_ATTENTION"

  const count = riskEstimation[riskEstimationType]?.count;

  const { risk, displayName } = riskEstimationList?.find(({ risk }) => risk === riskEstimationType) ?? {};

  const color = getRiskEstimationColor({ risk });
  return (
    <VerticallyAlignedFlexContainer style={{ justifyContent: 'center', width: '100%' }}>
      <Typography14 leftGutter={1} color={color}>
        {count}
      </Typography14>

      <Typography13 leftGutter={1} color={theme.palette.text.blackv1}>
        {displayName}
      </Typography13>
    </VerticallyAlignedFlexContainer>
  );
};

export const LevelUpAnalyticsCountChangeCell = ({ count, changeType, changePercentage }) => {
  if (Number.isNaN(count)) {
    return (
      <StyledGrid container justify="center" alignItems="center">
        <EmptyBar />
      </StyledGrid>
    );
  }
  return (
    <FlexContainer style={{ justifyContent: 'center' }}>
      <TextBodyMedium color="text.g1">{count}</TextBodyMedium>
    </FlexContainer>
  );
};

export const LevelUpAnalyticsFunctionCell = ({
  row,
  onExpandClick,
  isExpanded,
  canExpand,
  isSubRow = false,
  isLast = false,
  parentDepth = 0,
  isParentLastRow = false,
  levelsForWhichLineToBeDrawn = [],
}) => {
  const { value } = row ?? {};
  const { name: functionName, childFunctionIdsCount } = value ?? {};
  const isExpandable = canExpand;
  return (
    <VerticallyAlignedFlexContainer style={{ marginLeft: (parentDepth + 1) * 12 }}>
      {isSubRow && (
        <FlexContainer>
          <LevelUpAnalyticsLineMaker
            isLast={isLast}
            parentDepth={parentDepth}
            isExpanded={isExpanded}
            isParentLastRow={isParentLastRow}
            levelsForWhichLineToBeDrawn={levelsForWhichLineToBeDrawn}
          />
        </FlexContainer>
      )}
      <VerticallyAlignedFlexContainer>
        <ExpandIconContainer $isExpanded={isExpanded} $isExpandable={isExpandable}>
          <StyledIconButton style={{ zIndex: '5' }} onClick={onExpandClick}>
            <PlayIcon
              fillColor={isExpandable ? theme.palette.primary.main : theme.palette.grey.main}
              backgroundColor={isExpandable ? theme.palette.primary.main : theme.palette.grey.main}
              rotate={isExpanded}
            />
          </StyledIconButton>
          {isExpanded ? <DownLn $parentDepth={parentDepth} /> : null}
        </ExpandIconContainer>

        <FlexContainer style={{ flexDirection: 'column' }}>
          <Typography13 color={theme.palette.text.blackv1}>{functionName}</Typography13>
        </FlexContainer>
      </VerticallyAlignedFlexContainer>
    </VerticallyAlignedFlexContainer>
  );
};

export const LevelUpAnalyticsManagerCell = ({
  row,
  onExpandClick,
  isExpanded,
  canExpand,
  isSubRow = false,
  isLast = false,
  parentDepth = 0,
  isParentLastRow = false,
  levelsForWhichLineToBeDrawn = [],
}) => {
  const { value, imageUrl } = row ?? {};
  const { name, reporteeCount, isTeamManager } = value ?? {};
  const isExpandable = canExpand;
  return (
    <VerticallyAlignedFlexContainer style={{ marginLeft: parentDepth * 8 }}>
      {isSubRow && (
        <FlexContainer>
          <LevelUpAnalyticsLineMaker
            isLast={isLast}
            parentDepth={parentDepth}
            isExpanded={isExpanded}
            isParentLastRow={isParentLastRow}
            levelsForWhichLineToBeDrawn={levelsForWhichLineToBeDrawn}
          />
        </FlexContainer>
      )}
      <VerticallyAlignedFlexContainer>
        <ExpandIconContainer $isExpanded={isExpanded} $isExpandable={isExpandable}>
          <StyledIconButton style={{ zIndex: '5' }} onClick={onExpandClick}>
            <ArrowRightCircle
              fill="transparent"
              boxSize="16px"
              // onClick={toggleExpandedHandler}
              color="primary.1565BC"
            />
          </StyledIconButton>
          {isExpanded ? <DownLn $parentDepth={parentDepth} /> : null}
        </ExpandIconContainer>
        <Avatar src={imageUrl} sx={{ margin: '0 0.5rem ' }} name={name} />
        <FlexContainer style={{ flexDirection: 'column' }}>
          <TextBodyMedium color="text.g1">{name}</TextBodyMedium>
          <TextLabel color="text.g2">{isTeamManager ? 'Team Manager' : `${reporteeCount} Members`}</TextLabel>
        </FlexContainer>
      </VerticallyAlignedFlexContainer>
    </VerticallyAlignedFlexContainer>
  );
};
export const LevelUpAnalyticsIndividualCell = ({
  row,
  isSubRow = false,
  isLast = false,
  parentDepth = 0,
  isParentLastRow = false,
  levelsForWhichLineToBeDrawn = [],
}) => {
  const { value, imageUrl } = row ?? {};
  const { name, reporteeCount, isTeamManager } = value ?? {};
  return (
    <VerticallyAlignedFlexContainer>
      <VerticallyAlignedFlexContainer>
        <Avatar src={imageUrl} sx={{ marginRight: '0.5rem' }} name={name} />
        <FlexContainer style={{ flexDirection: 'column' }}>
          <TextBodyMedium color="text.g1">{name}</TextBodyMedium>
        </FlexContainer>
      </VerticallyAlignedFlexContainer>
    </VerticallyAlignedFlexContainer>
  );
};

const DownLn = emotionStyled.div`
  position: absolute;
  width: 1px;
  top: 64%;
  bottom: 0;
  background: ${(props) => props.theme.colors.text.g3};

  left: ${(props) => `${(props.$parentDepth + 1) * 23.5 + props.$parentDepth * 13}px`};
`;
const ExpandIconContainer = styled.div`
  svg {
    transform: rotate(${(props) => (props.$isExpanded ? '90deg' : '0deg')});
    transition: transform 150ms;
  }
  margin-right: 16px;
  ${(props) =>
    !props.$isExpandable &&
    css`
      opacity: 0.4;
      button {
        cursor: no-drop;
      }
    `}
`;

export const RoleDepartmentCell = ({ role = '', department = '' }) => (
  <FlexContainer style={{ flexDirection: 'column' }}>
    <Typography13 color={theme.palette.text.blackv1}>{role}</Typography13>
    <Typography12 color={theme.palette.text.dark1}>{department}</Typography12>
  </FlexContainer>
);
